import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { localized, fileUrl } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProducersMap } from "@/components/ProducersMap";
import { MapPin, Mail, Globe } from "lucide-react";

export default function VendorStore() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id } = useParams();
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get(`/vendors/${id}`).then((r) => setVendor(r.data)).catch(() => {});
    api.get("/products", { params: { vendor_id: id } }).then((r) => setProducts(r.data)).catch(() => {});
  }, [id]);

  if (!vendor) return <div className="max-w-7xl mx-auto px-4 py-16 text-ink-muted">Loading…</div>;

  return (
    <div data-testid="vendor-store-page">
      <section className="relative h-[420px] overflow-hidden">
        {vendor.cover_image && <img src={fileUrl(vendor.cover_image)} alt={vendor.farm_name} className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/30 via-transparent to-bone" />
      </section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative">
        <div className="bg-bone rounded-sm p-10 hairline">
          <p className="overline">{localized(vendor, "region", lang)}</p>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-2" data-testid="vendor-name">{localized(vendor, "farm_name", lang)}</h1>
          <p className="text-ink-soft leading-relaxed mt-6 max-w-3xl text-lg" data-testid="vendor-story">{localized(vendor, "story", lang)}</p>
          {(vendor.contact_email || vendor.website) && (
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm" data-testid="vendor-contact">
              {vendor.contact_email && (
                <a
                  href={`mailto:${vendor.contact_email}`}
                  className="flex items-center gap-2 text-ink-soft hover:text-olive transition-colors"
                  data-testid="vendor-contact-email"
                >
                  <Mail className="w-4 h-4 text-olive" />
                  <span>{vendor.contact_email}</span>
                </a>
              )}
              {vendor.website && (
                <a
                  href={vendor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-ink-soft hover:text-olive transition-colors"
                  data-testid="vendor-website"
                >
                  <Globe className="w-4 h-4 text-olive" />
                  <span>{vendor.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Location map */}
      {typeof vendor.lat === "number" && typeof vendor.lng === "number" && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="vendor-location-section">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <p className="overline flex items-center gap-2"><MapPin className="w-3 h-3" />{t("vendors.estateOverline")}</p>
              <h2 className="font-serif text-3xl mt-2">{t("vendors.estateTitle")}</h2>
            </div>
            <a
              href={`https://www.openstreetmap.org/?mlat=${vendor.lat}&mlon=${vendor.lng}#map=11/${vendor.lat}/${vendor.lng}`}
              target="_blank" rel="noreferrer"
              className="text-sm text-olive hover:underline"
              data-testid="vendor-map-osm-link"
            >
              {t("vendors.openInOsm")}
            </a>
          </div>
          <ProducersMap vendors={[vendor]} center={[vendor.lat, vendor.lng]} zoom={11} height="380px" />
          <p className="text-xs text-ink-muted mt-3 font-mono">
            {vendor.lat.toFixed(4)}°N, {vendor.lng.toFixed(4)}°E
          </p>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="font-serif text-3xl mb-8">{t("vendors.fromEstate")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="vendor-products">
          {products.map((p) => <ProductCard key={p.product_id} product={p} testid="vendor-product" />)}
        </div>
        {products.length === 0 && <p className="text-ink-muted">No products yet.</p>}
      </section>
    </div>
  );
}
